<template>
    <McForm
        ref="sgFormRef"
        :model="innerValue"
        :rules="sgFormRules"
        :label-width="120"
    >
        <McFormItem
            label="外卖商品类目"
            prop="sgBgCategoryId"
        >
            <SgCategorySelect
                ref="sgCategorySelectRef"
                v-model="innerValue.sgBgCategoryId"
                :disabled="disabled"
                @change="changeCategory"
            />

            <SgRecommend
                :recommend-list="sgRecommend['bgCategoryId'] || []"
                attr-type="fixed"
                :selected-values.sync="innerValue.bgCategoryId"
            />
        </McFormItem>

        <SgExtAttrWrap
            ref="sgExtAttrWrapRef"
            v-model="innerValue.extAttrs"
            :disabled="disabled"
        />
    </McForm>
</template>

<script>
import { computed, getCurrentInstance, ref } from '@vue/composition-api';
import { hPost } from '@mcfe/tools';
import Rules from '@mcfe/tools-rules';
import SgCategorySelect from '../../../../flashSalesInfoManage/components/SgCategorySelect';
import SgExtAttrWrap from '../../../../flashSalesInfoManage/components/SgExtAttrWrap';
import SgRecommend from '../../../../flashSalesInfoManage/components/SgRecommend';

export default {
    components: {
        SgCategorySelect,
        SgExtAttrWrap,
        SgRecommend,
    },

    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        skuName: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    setup(props) {
        const { proxy } = getCurrentInstance();
        const sgFormRef = ref(null);
        const sgCategorySelectRef = ref(null);
        const sgExtAttrWrapRef = ref(null);
        // 闪购推荐值
        const sgRecommend = ref({
            bgCategoryId: 0,
            sgExtAttrItems: [],
            weightQuantity: null,
            weightUnit: null,
        });

        const sgFormRules = { sgBgCategoryId: [Rules.isRequired('请选择商品类目')] };

        const innerValue = computed({
            get() {
                return props.value;
            },
            set(val) {
                proxy.$emit('input', val);
            },
        });

        // 处理推荐值
        const handleRecommendValues = (key, value) => {
            if (key === 'bgCategoryId' && value) {
                const sgCategory = sgCategorySelectRef.value.mateSgCategory(value);
                if (!sgCategory) return [];
                return [{ id: sgCategory?.id, value: sgCategory?.name }];
            }
            if (value) {
                return [{ id: value, value }];
            }
            return [];
        };

        // 查询闪购商品推荐值
        const queryRecommendValueForSgProduct = async (bgCategoryId, skuName) => {
            if (!skuName && !props.skuName) return;
            // 构造请求参数，如果内部值中没有后台类目ID和商品名称，则使用参数中的值
            const params = {
                bgCategoryId: bgCategoryId || innerValue.value?.bgCategoryId,
                skuName: skuName || props.skuName,
            };
            // 发送请求，获取闪购商品推荐值
            const res = await hPost('/api/vss/shepherd/product/bizcenter/SgProductCommandTService/queryRecommendValueForSgProduct', params);
            // 获取返回的推荐值的所有键
            const sgRecommendKey = Object.keys(res?.sgProductRecommendValues || {});
            // 遍历所有键，处理每个键对应的推荐值
            sgRecommendKey.forEach((key) => { sgRecommend.value[key] = handleRecommendValues(key, res.sgProductRecommendValues[key]); });
            // 如果返回的推荐值中包含扩展属性项
            if (res.sgProductRecommendValues?.sgExtAttrItems?.length) {
                // 遍历内部值的扩展属性，为每个属性添加推荐值
                innerValue.value.extAttrs = (innerValue.value?.extAttrs || []).map((item) => {
                    // 在返回的推荐值的扩展属性项中查找与当前项匹配的属性
                    const recommend = res.sgProductRecommendValues?.sgExtAttrItems.find((items) => item.attrId === items.attrId);
                    // 返回处理后的属性对象，包含推荐值
                    return {
                        ...item,
                        recommendValues: recommend?.sgExtAttrValues || [],
                    };
                });
            }
        };

        // 查询闪购后台扩展属性
        const querySgBgCategoryExtAttr = async (bgCategoryId) => {
            // 如果内部值中没有后台类目ID并且参数中也没有后台类目ID，则直接返回
            if (!bgCategoryId && !innerValue.value.bgCategoryId) return;
            // 构造请求参数
            const params = { bgCategoryId: bgCategoryId || innerValue.value.bgCategoryId };
            // 发送请求，获取闪购后台扩展属性
            const res = await hPost('/api/vss/shepherd/product/bizcenter/SgProductCommandTService/querySgBgCategoryExtAttr', params);
            // 处理返回的扩展属性数据
            const extAttr = res.sgExtAttrItems.map((item) => {
                // 在内部值的扩展属性中查找与当前项匹配的属性
                const attrItems = (innerValue.value?.extAttrs || []).find((items) => items.attrId === item.attrId);
                // 根据属性值类型，映射为内部的值类型 闪购 1:单选 2:多选 3:input 买菜 3:单选 4:多选 1和2: input
                const valueTypeMapping = { 1: 3, 2: 4, default: 2 };
                const valueType = valueTypeMapping[item.attrValueType] || valueTypeMapping.default;
                // 返回处理后的属性对象
                return {
                    attrId: item.attrId,
                    attrName: item.attrName,
                    valueType,
                    required: item.need === 1,
                    selectedValues: attrItems?.sgExtAttrValues || [],
                    selectValues: item.sgExtAttrValues.map((items) => ({ ...items, valid: true })),
                };
            });
            innerValue.value.extAttrs = extAttr;
            queryRecommendValueForSgProduct(bgCategoryId);
        };

        // 匹配层级
        const mateLevel = (val) => {
            const sgCategory = sgCategorySelectRef.value.mateSgCategory(val);
            return sgCategory.level;
        };

        // 类目change
        const changeCategory = (val) => {
            // 重新获取扩展属性
            querySgBgCategoryExtAttr(val);
            innerValue.value.sgBgCategoryLevel = mateLevel(val);
        };

        // 验证表单和扩展属性
        const validate = async () => {
            // 验证表单，如果验证通过则返回 true，否则返回 false
            const validateForm = sgFormRef.value.validate().then(() => true).catch(() => false);
            // 验证扩展属性，如果扩展属性存在则进行验证，否则直接返回 true
            const validateExtAttr = sgExtAttrWrapRef.value ? sgExtAttrWrapRef.value.validate() : Promise.resolve(true);
            // 等待所有验证完成
            const valids = await Promise.all([validateForm, validateExtAttr]);
            // 如果所有验证都通过，则返回 true，否则返回 false
            return valids.every((valid) => valid);
        };

        return {
            sgFormRef,
            sgCategorySelectRef,
            sgExtAttrWrapRef,
            sgRecommend,
            innerValue,
            sgFormRules,
            changeCategory,
            handleRecommendValues,
            queryRecommendValueForSgProduct,
            querySgBgCategoryExtAttr,
            mateLevel,
            validate,
        };
    },
};
</script>
